import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { ErrorState, Loading, Modal, PriorityBadge, StatusBadge } from './ui.tsx';
import { PRIORITY_LABEL, STATUS_LABEL, formatDate, formatDateTime } from '../lib/format.ts';
import type { Action, ActionDetail, ActionPriority, ActionStatus } from '../../../shared/types.ts';

const COLUMNS: ActionStatus[] = ['pendente', 'em_andamento', 'concluida'];
const PRIORITIES: ActionPriority[] = ['urgente', 'alta', 'media', 'baixa'];

/** Modal de edição rápida de uma ação — usado em Ações e em qualquer lista
 * que referencie uma ação (Visão Geral) sem forçar navegação até /acoes. */
export function ActionDetailModal({
  id,
  canEdit,
  onClose,
  onChanged,
}: {
  id: string;
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detail = useAsync(() => api.get<ActionDetail>(`/actions/${id}`), [id]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  async function patch(changes: Partial<Action>) {
    setBusy(true);
    try {
      await api.patch(`/actions/${id}`, changes);
      detail.reload();
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function addComment() {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await api.post(`/actions/${id}/comments`, { body: comment.trim() });
      setComment('');
      detail.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={detail.data?.title ?? 'Ação'} onClose={onClose} wide>
      {detail.loading && <Loading label="Carregando ação…" />}
      {detail.error && <ErrorState message={detail.error} onRetry={detail.reload} />}

      {detail.data && (
        <div className="stack">
          {detail.data.description && (
            <p className="text-body detail-description">{detail.data.description}</p>
          )}

          <dl className="detail-grid">
            <div>
              <dt className="text-micro text-muted">Situação</dt>
              <dd>
                {canEdit ? (
                  <select
                    className="select"
                    value={detail.data.status}
                    disabled={busy}
                    onChange={(e) => void patch({ status: e.target.value as ActionStatus })}
                  >
                    {COLUMNS.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <StatusBadge status={detail.data.status} />
                )}
              </dd>
            </div>

            <div>
              <dt className="text-micro text-muted">Prioridade</dt>
              <dd>
                {canEdit ? (
                  <select
                    className="select"
                    value={detail.data.priority}
                    disabled={busy}
                    onChange={(e) => void patch({ priority: e.target.value as ActionPriority })}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_LABEL[p]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <PriorityBadge priority={detail.data.priority} />
                )}
              </dd>
            </div>

            <div>
              <dt className="text-micro text-muted">Prazo</dt>
              <dd>
                {canEdit ? (
                  <input
                    className="input"
                    type="date"
                    value={detail.data.dueDate ?? ''}
                    disabled={busy}
                    onChange={(e) => void patch({ dueDate: e.target.value || null })}
                  />
                ) : (
                  formatDate(detail.data.dueDate)
                )}
              </dd>
            </div>

            <div>
              <dt className="text-micro text-muted">Origem</dt>
              <dd className="text-secondary">
                {detail.data.source === 'auditoria'
                  ? `Auditoria · ${detail.data.sourceRef ?? ''}`
                  : `Criada por ${detail.data.createdBy?.name ?? '—'}`}
              </dd>
            </div>
          </dl>

          <section>
            <h3 className="text-label">Comentários</h3>
            {detail.data.comments.length === 0 ? (
              <p className="text-muted text-micro">Nenhum comentário ainda.</p>
            ) : (
              <ul className="comment-list">
                {detail.data.comments.map((c) => (
                  <li key={c.id}>
                    <div className="comment-list__head">
                      <strong className="text-label">{c.author?.name ?? 'Alguém'}</strong>
                      <span className="text-micro text-muted">{formatDateTime(c.createdAt)}</span>
                    </div>
                    <p className="text-body">{c.body}</p>
                  </li>
                ))}
              </ul>
            )}

            {canEdit && (
              <div className="comment-form">
                <textarea
                  className="textarea"
                  rows={2}
                  placeholder="Escreva um comentário…"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => void addComment()}
                  disabled={busy || !comment.trim()}
                >
                  Comentar
                </button>
              </div>
            )}
          </section>

          {detail.data.history.length > 0 && (
            <section>
              <h3 className="text-label">Histórico</h3>
              <ul className="history-list">
                {detail.data.history.map((h) => (
                  <li key={h.id} className="text-micro text-muted">
                    {formatDateTime(h.createdAt)} — {h.field}: {h.oldValue ?? '—'} → {h.newValue ?? '—'}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
